import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { markDeliveryFulfilledFormSchema, VehicleFulfillmentStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Checkbox,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGetMarkDeliveryFulfilled } from '../../../api/progress/useGetMarkDeliveryFulfilled';
import { useMarkDeliveryFulfilled } from '../../../api/progress/useMarkDeliveryFulfilled';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkDeliveryFulfilledDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkDeliveryFulfilledDetail component for marking a vehicle delivery as fulfilled.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkDeliveryFulfilledDetail = ({ actionItem }: MarkDeliveryFulfilledDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markDeliveryFulfilledMutation = useMarkDeliveryFulfilled(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetMarkDeliveryFulfilled(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markDeliveryFulfilledFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.vehicleFulfillmentStatus === VehicleFulfillmentStatus.FULFILLED) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markDeliveryFulfilledMutation.mutateAsync();
			toast({
				message: 'Delivery marked as fulfilled successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to mark delivery as fulfilled';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the delivery has been completed and the vehicle has been delivered to the buyer. Note: an email will be sent to the buyer communicating the updated status.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.vehicleFulfillmentStatus ===
				  VehicleFulfillmentStatus.FULFILLED ? (
					<Text size='body' color='success'>
						✓ Delivery already marked as fulfilled
					</Text>
				) : null}

				{/* Show form if not complete */}
				{existingData?.vehicleFulfillmentStatus !== VehicleFulfillmentStatus.FULFILLED && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												markDeliveryFulfilledMutation.isPending
											}
											ariaLabel='Confirm delivery is fulfilled'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markDeliveryFulfilledMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark delivery as fulfilled'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
